import React from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import DisabledReason from '../system/DisabledReason'
import { CARD_SX, PILL_RADIUS } from '../../agent/components/surface'
import { readerLines } from '../../utils/guided'
import type { Guide, GuidePoint, GuideReader } from '../../types/gotypes'

export interface GuideIntroProps {
  guide: Pick<Guide, 'title' | 'goal' | 'intro' | 'readers_note'>
  points: GuidePoint[]
  /** The reviewers a Manager named, so the person knows who reads their notes. */
  reviewers: GuideReader[]
  /** Start (a new invitation) or Continue. */
  startLabel: string
  busy?: boolean
  /** Why the person cannot start; undefined when they can. */
  startDisabledReason?: string
  error?: string
  onStart: () => void
  onOpenAgenda: () => void
}

/**
 * What a person sees before the conversation: what it is for, who reads the
 * answers (always: the Managers, the named reviewers, then the guide's own
 * note), and the agenda in full, so the structure is set before they
 * begin. Download agenda opens the print view for reading ahead.
 */
const GuideIntro: React.FC<GuideIntroProps> = ({ guide, points, reviewers, startLabel, busy, startDisabledReason, error, onStart, onOpenAgenda }) => (
  <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 4, backgroundColor: 'background.default' }}>
    <Box data-testid="guide-intro" sx={{ ...CARD_SX, maxWidth: 760, mx: 'auto', p: { xs: 2.5, sm: 4 } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'info.main' }}>
        Guided conversation
      </Typography>
      <Typography component="h1" sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em', mt: 0.5 }}>
        {guide.title}
      </Typography>
      {guide.intro && (
        <Typography sx={{ mt: 2, fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{guide.intro}</Typography>
      )}
      {guide.goal && (
        <Box sx={{ mt: 3 }}>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700 }}>What this is for</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.6, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{guide.goal}</Typography>
        </Box>
      )}
      <Box sx={{ mt: 3 }} data-testid="guide-readers">
        <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700 }}>Who reads your answers</Typography>
        {[...readerLines(reviewers), ...(guide.readers_note ? [guide.readers_note] : [])].map((line) => (
          <Typography key={line} sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.6, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{line}</Typography>
        ))}
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700 }}>The agenda</Typography>
        <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {points.map((p) => (
            <Box component="li" key={p.id}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.title}</Typography>
              {p.question && <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5 }}>{p.question}</Typography>}
            </Box>
          ))}
        </Box>
      </Box>
      <Typography sx={{ mt: 3, fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>
        The agent asks about one point at a time and writes notes as you go. You can type, use the microphone to talk at length, or attach a file.
        You can skip a point and come back to it, correct any note, and stop and carry on another day.
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
        <DisabledReason reason={startDisabledReason}>
          <Button variant="contained" color="info" disableElevation onClick={onStart} disabled={busy} sx={{ borderRadius: PILL_RADIUS, px: 3 }}>
            {busy ? 'Starting…' : startLabel}
          </Button>
        </DisabledReason>
        <Button color="info" variant="outlined" onClick={onOpenAgenda} sx={{ borderRadius: PILL_RADIUS }}>
          Download agenda (PDF)
        </Button>
      </Box>
    </Box>
  </Box>
)

export default GuideIntro
